import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { copyMppModalData } from '@app/mpp/model/copy-mpp-model';
import {
  cardClickEvent,
  mppRecipeCard,
} from '@app/mpp/model/recipe-card-model';
import { Recipe, State } from '@usf/mpp-types';
import { TranslateService } from '@ngx-translate/core';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  DualPaneView,
  MultiInputItems,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  InputLabelItems,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import {
  dualPaneBasicSelection,
  dualPaneDefaultVerifySave,
  getSearchableObservable$,
  handleInputOrRangeValueSave,
  singleSelectRadioSearchDefault,
  updateRightPaneWithValue,
  updateRightPaneWithValueForMaps,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import { BehaviorSubject, of } from 'rxjs';
import { UserKinds } from '@usf/user-types/user';
import { trimSpaceForInput } from '@shared/helpers/input-cleanup.helpers';

@Component({
  selector: 'app-copy-recipe-create-modal',
  templateUrl: './copy-recipe-create-modal.component.html',
  styleUrls: ['./copy-recipe-create-modal.component.scss'],
})
export class CopyRecipeCreateModalComponent extends BaseDualPaneModalContainerComponent {
  @Output() dismissEvent: EventEmitter<void> = new EventEmitter();
  @Output() submitEvent: EventEmitter<cardClickEvent> = new EventEmitter();
  @Input() platform: PlatformEnum;
  @Input() data: copyMppModalData;
  @Input() translateService: TranslateService;
  @Input() userKind: UserKinds;
  hasError = false;

  platformRef = PlatformEnum;
  constructor(public platformService: PlatformService) {
    super(platformService);
  }

  recipeState = [
    {
      label: 'i18n.menuProfitPro.copyModal.state.internal',
      value: State.INTERNAL,
      selected: false,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.menuProfitPro.copyModal.state.public',
      value: State.PUBLIC,
      selected: true,
      type: MultiSelectItems.basic,
    },
  ];

  createCopyRecipeVM = () => {
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'copy-recipe',
      title: 'i18n.menuProfitPro.copyModal.title',
      submitText: this.translateService.instant('i18n.common.create'),
      backText: this.translateService.instant('i18n.common.save'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildSelectCopyRecipeRightPane,
      handleSubmit: this.submitCopyRecipeModalValues,
      verifySubmit: this.verifySubmission,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const nameMap = new Map<number, any>();
    nameMap.set(0, this.getNewRecipeName());

    const contentItems = [
      {
        label: 'i18n.menuProfitPro.copyModal.selectCopyRecipe.label',
        subLabel: this.data.recipeCopy.recipeName,
        view: DualPaneView.searchableItemRadio,
        selected: true,
        value: this.data.recipeCopy.recipeName,
        onSelectfunction: this.buildSelectCopyRecipeRightPane,
      },
      {
        label: 'i18n.menuProfitPro.copyModal.newRecipeName.label',
        subLabel: this.getNewRecipeName(),
        view: DualPaneView.multiInput,
        selected: false,
        value: nameMap,
        onSelectfunction: this.buildNewNameRightPane,
      },
    ];

    if (this.userKind !== UserKinds.External) {
      contentItems.push({
        label: 'i18n.menuProfitPro.copyModal.recipeType.label',
        subLabel: 'i18n.menuProfitPro.copyModal.state.public',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        value: State.PUBLIC,
        onSelectfunction: this.buildRecipeStateRightPane,
      });
    }

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
  };

  buildSelectCopyRecipeRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let val;
    if (!viewModel?.leftView) {
      val = this.data.recipeCopy.recipeName;
    } else {
      val = viewModel.leftView.content[0].value;
    }
    const itemsObservable$ = of(
      this.convertRecipesToRadioItems(this.data.recipes, this.data.recipeCopy),
    );
    const selectCopyRecipeModal = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.menuProfitPro.copyModal.selectCopyRecipe.label',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: false,
        onSearchFunction: this.singleSelectRadioSearchWithoutSublabelSearch,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        noResultsOverride:
          'i18n.menuProfitPro.copyModal.selectCopyRecipe.searchResult',
        searchPlaceholder:
          'i18n.menuProfitPro.copyModal.selectCopyRecipe.searchPlaceholder',
      },
      value: val,
      itemsObservable$,
      handleItemSelection: this.recipeSelection,
    } as RightPaneInfo;
    selectCopyRecipeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(selectCopyRecipeModal);
    updateRightPaneWithValue(viewModel, paneSelected, selectCopyRecipeModal);
    return selectCopyRecipeModal;
  };

  convertRecipesToRadioItems = (
    recipes: mppRecipeCard[],
    recipeCopy: Recipe,
  ) => {
    return recipes.map(recipe => {
      return {
        label: recipe?.recipeName,
        value: recipe?.recipeName,
        subLabel: recipe.ingredientList?.length + ' Ingredients',
        selected:
          recipe?.recipeName.toUpperCase() ===
          recipeCopy.recipeName.toUpperCase(),
        type: MultiSelectItems.basic,
      };
    });
  };

  singleSelectRadioSearchWithoutSublabelSearch = (
    rightPane: RightPaneInfo,
    searchKey: string,
    items: any[],
  ) => {
    return items.filter(item => {
      return item?.label
        ?.toLowerCase()
        ?.includes((searchKey || '').toLowerCase());
    });
  };

  recipeSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    left.content[0] = {
      ...left.content[0],
      subLabel: value,
      value: value,
    };
    right.value = value;
    left.content[1] = {
      ...left.content[1],
      subLabel: value,
      value: new Map<number, string>([[0, value]]),
    };
  };

  buildNewNameRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let inputValue = this.getNewRecipeName(viewModel);
    if (!!viewModel?.leftView?.content[1].value) {
      inputValue = viewModel?.leftView?.content[1].value.get(0);
    }
    const nameModal = {
      view: DualPaneView.multiInput,
      title: 'i18n.menuProfitPro.copyModal.newRecipeName.label',
      inputsPane: {
        items: [
          {
            type: MultiInputItems.input,
            label: 'i18n.menuProfitPro.copyModal.newRecipeName.label',
            inputcleanup: this.trimSpaceTo100,
            maxChar: 100,
            hasError: false,
            onInput: this.onNameInput,
            inputValue,
          },
        ],
        handleValueUpdate: handleInputOrRangeValueSave,
      },
      verifySaveButton: this.verifyNewName,
      overrideSaveOnNavigation: this.saveNewName,
    } as RightPaneInfo;
    updateRightPaneWithValueForMaps(viewModel, paneSelected, nameModal);
    return nameModal;
  };

  trimSpaceTo100 = (value: any) => {
    return trimSpaceForInput(value.toString().substring(0, 100));
  };

  onNameInput = (item: InputLabelItems, value: any) => {
    item.hasError = this.hasEmoji(value);
    this.hasError = item.hasError;
    if (item.hasError) {
      item.errorMessage =
        'i18n.menuProfitPro.copyModal.newRecipeName.invalidCharacterError';
    }
    item.inputValue = !!item.inputcleanup ? item.inputcleanup(value) : value;
    return item.inputValue;
  };

  hasEmoji(text: string): boolean {
    return /\p{Extended_Pictographic}/gu.test(text);
  }

  verifyNewName = (vm: DualPaneViewModel) => {
    return !!vm?.rightView.value?.get(0) && vm.rightView.value.get(0) !== '';
  };

  saveNewName = (vm: DualPaneViewModel, item: BasicListItem, value: any) => {
    let subLabel = '';
    if (value) {
      subLabel = value.get(0);
    }
    item.value = value;
    item.subLabel = subLabel;
    return item;
  };

  getNewRecipeName = (viewModel?: DualPaneViewModel) => {
    return viewModel?.leftView?.content[1]?.subLabel
      ? viewModel.leftView.content[1].subLabel
      : this.data.recipeCopy.recipeName;
  };

  buildRecipeStateRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const listTyrecipeStateModal = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.menuProfitPro.copyModal.recipeType.label',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
      },
      itemsObservable$: of(this.recipeState),
      handleItemSelection: dualPaneBasicSelection,
    } as RightPaneInfo;
    listTyrecipeStateModal.searchPane.searchableObservable$ =
      getSearchableObservable$(listTyrecipeStateModal);
    updateRightPaneWithValue(viewModel, paneSelected, listTyrecipeStateModal);
    return listTyrecipeStateModal;
  };

  verifySubmission = (vm: DualPaneViewModel) => {
    let valid = true;
    vm.leftView.content.forEach((item, index) => {
      if ((index === 0 || index === 2) && !item.value) {
        valid = false;
      }
      if (
        index === 1 &&
        (!item.value ||
          item.value.length === 0 ||
          item.value.get(0) === '' ||
          this.hasError)
      ) {
        valid = false;
      }
    });
    return valid;
  };

  openModal = () => {
    this.createCopyRecipeVM();
    this.isModalOpen$.next(true);
  };

  submitCopyRecipeModalValues = (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();
    const updatedRecipeCopy = { ...this.data.recipeCopy };

    updatedRecipeCopy.recipeName = vm.leftView.content[1]?.value.get(0);

    if (this.userKind !== UserKinds.External) {
      updatedRecipeCopy.state = vm.leftView.content[2]?.value.toLowerCase();
    }
    submitMap.set('updated-copy', updatedRecipeCopy);
    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      this.submitEvent.emit(this.submit$.value.get('updated-copy'));
    }
  }

  closeModal = () => {
    this.isModalOpen$.next(false);
  };
}
